<script setup>
import { Link, usePage } from '@inertiajs/vue3'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { BellIcon } from '@heroicons/vue/24/outline'
import { computed, onMounted, ref } from 'vue'
import { useAuthStore } from '@stores/AuthStore.js'
import { useNotificationsStore } from '@stores/NotificationsStore.js'

const authStore = useAuthStore()
const store = useNotificationsStore()
const page = usePage()

// Same gate + hydration guard as AccountMenu: the feed is client-authed, and the
// AuthStore is empty during SSR.
const loginEnabled = computed(() => !!page.props.features?.subscription_login)
const mounted = ref(false)
const isAuthed = computed(() => mounted.value && authStore.isAuthenticated)

onMounted(() => {
  mounted.value = true
  if (authStore.isAuthenticated) store.load()
})

const badge = computed(() => (store.unread > 9 ? '9+' : String(store.unread)))

// Where each notification takes the client. Content-free deliveries only carry a
// type + deep-link ids, so we route by type.
const hrefFor = (type) =>
  ({
    new_message: '/messages',
    document_requested: '/documents',
    task_reminder: '/documents',
    payment_action_required: '/subscription',
    trial_ending: '/subscription',
  })[type] ?? '/account'

const relativeTime = (iso) => {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.round(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 7) return `${days}d ago`
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}
</script>

<template>
  <Menu v-if="isAuthed && loginEnabled" as="div" class="relative">
    <MenuButton
      dusk="notification-bell"
      aria-label="Notifications"
      class="relative flex items-center rounded-md p-2 text-white hover:bg-gray-900 hover:bg-opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
    >
      <BellIcon class="h-6 w-6 shrink-0" />
      <span
        v-if="store.unread > 0"
        dusk="notification-badge"
        class="absolute -right-0.5 -top-0.5 flex min-w-[1.15rem] items-center justify-center rounded-full bg-indigo-500 px-1 text-[11px] font-semibold leading-5 text-white ring-2 ring-gray-900"
      >
        {{ badge }}
      </span>
    </MenuButton>

    <MenuItems
      class="absolute right-0 z-50 mt-2 w-80 origin-top-right overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
    >
      <div class="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
        <p class="text-sm font-semibold text-gray-900">Notifications</p>
        <button
          v-if="store.unread > 0"
          type="button"
          dusk="notification-mark-read"
          :disabled="store.marking"
          class="text-xs font-medium text-indigo-600 hover:text-indigo-500 disabled:opacity-50"
          @click="store.markAllRead()"
        >
          Mark all read
        </button>
      </div>

      <div class="max-h-96 overflow-y-auto py-1">
        <p v-if="store.loading" class="px-4 py-6 text-center text-sm text-gray-400">Loading…</p>

        <p
          v-else-if="store.loadError"
          class="px-4 py-6 text-center text-sm text-gray-500"
        >
          Couldn't load your notifications.
        </p>

        <p
          v-else-if="store.items.length === 0"
          class="px-4 py-8 text-center text-sm text-gray-400"
        >
          You're all caught up.
        </p>

        <template v-else>
          <MenuItem v-for="item in store.items" :key="item.id" v-slot="{ active }">
            <Link
              :href="hrefFor(item.type)"
              :class="[active ? 'bg-gray-50' : '', 'flex gap-3 px-4 py-3']"
              @click="store.markAllRead()"
            >
              <span
                class="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                :class="item.read ? 'bg-transparent' : 'bg-indigo-500'"
                aria-hidden="true"
              />
              <span class="min-w-0">
                <span class="block text-sm text-gray-800">{{ item.message }}</span>
                <span class="mt-0.5 block text-xs text-gray-400">{{ relativeTime(item.created_at) }}</span>
              </span>
            </Link>
          </MenuItem>
        </template>
      </div>
    </MenuItems>
  </Menu>
</template>
